import { utf8Bytes } from "../lib/sanitize";
import { BACKOFF_MS, BATCH_EVENTS, BODY_MAX_BYTES, REISSUE_MIN_MS } from "./config";
import type { Outbox } from "./outbox";
import type { Identity, OutboxEntry, OutboxRecord, SessionHeader } from "./types";

const MAX_PART = 50;
const REQUESTS_PER_FLUSH = 5;
const CONTENT_TYPE = "text/plain;charset=UTF-8";

export interface TransportDeps {
    url: string;
    outbox: Outbox;
    identity: (fresh?: boolean) => Identity;
    headerAsync: (rec: OutboxRecord) => Promise<SessionHeader>;
    headerSync: (rec: OutboxRecord) => SessionHeader;
    /** 204 или read_only_impersonation: молчать до смены владельца. */
    onServerSuspend: (owner: string) => void;
    /** 400 bad_session / stale_session: сеанс надо начать заново. */
    onStaleSession: (sid: string) => void;
    reissue: () => Promise<boolean>;
    now?: () => number;
    fetchImpl?: typeof fetch;
    beaconImpl?: (url: string, data: Blob) => boolean;
    warn?: (msg: string, extra?: unknown) => void;
}

type Step = "continue" | "stop";

const seqsOf = (batch: OutboxEntry[]): Set<number> => new Set(batch.map((e) => e.ev.seq));

/**
 * Отправка журнала на воркер: text/plain (простой CORS‑запрос, без preflight),
 * не больше одного запроса одновременно, отступ при ошибках. Событие
 * удаляется из очереди только по ответу 200 — маяк при уходе со страницы ничего
 * не удаляет, повтор сервер отбросит по seq.
 */
export class Transport {
    private inFlight: Promise<void> | null = null;
    private backoffIdx = -1;
    private nextAt = 0;
    private notDeployed = false;
    private lastReissueAt = -Infinity;
    private readonly maxBatch = new Map<string, number>();
    lastKey: string | null = null;

    constructor(private readonly deps: TransportDeps) {}

    private now(): number {
        return (this.deps.now || Date.now)();
    }

    private get fetchImpl(): typeof fetch {
        return this.deps.fetchImpl || ((...a: Parameters<typeof fetch>) => fetch(...a));
    }

    backingOff(): boolean {
        return this.now() < this.nextAt;
    }

    /**
     * `as` — отправить очередь конкретного владельца его токеном, взятым заранее:
     * при выходе и перед «войти как» ключи в localStorage вот-вот сменятся.
     */
    flush(o: { force?: boolean; as?: { email: string; token: string } } = {}): Promise<void> {
        if (this.inFlight) {
            const as = o.as;
            return as ? this.inFlight.then(() => this.flush({ force: true, as })) : this.inFlight;
        }
        if (!o.force && this.backingOff()) return Promise.resolve();
        this.inFlight = this.run(o.as).finally(() => {
            this.inFlight = null;
        });
        return this.inFlight;
    }

    private async run(as?: { email: string; token: string }): Promise<void> {
        for (let i = 0; i < REQUESTS_PER_FLUSH; i++) {
            // Личность и токен читаем до первого await.
            const id: Identity = as
                ? { email: as.email, token: as.token, suspended: false }
                : this.deps.identity(i > 0);
            if (id.suspended || !id.email || !id.token) return;
            const rec = this.deps.outbox.sendable(id.email)[0];
            if (!rec) return;
            const step = await this.sendOne(rec, id.token, !as);
            if (step === "stop") return;
        }
    }

    private async sendOne(rec: OutboxRecord, token: string, mayReissue: boolean): Promise<Step> {
        const { outbox } = this.deps;
        const batch = outbox.takeBatch(rec, this.maxBatch.get(rec.sid) ?? BATCH_EVENTS);
        if (batch.length === 0) return "stop";
        let res: Response;
        try {
            const header = await this.deps.headerAsync(rec);
            const body = JSON.stringify({ token, session: header, events: batch.map((e) => e.ev) });
            res = await this.fetchImpl(this.deps.url, {
                method: "POST",
                body,
                credentials: "omit",
                headers: { "Content-Type": CONTENT_TYPE },
            });
        } catch {
            this.backoff();
            return "stop";
        }
        const status = res.status;
        const json = ((await res.json().catch(() => null)) || {}) as {
            error?: unknown;
            key?: unknown;
            full?: unknown;
            ack_through?: unknown;
        };
        const error = typeof json.error === "string" ? json.error : "";

        if (status === 200) {
            this.resetBackoff();
            if (typeof json.key === "string") this.lastKey = json.key;
            if (json.full === true) {
                const through = typeof json.ack_through === "number" ? json.ack_through : 0;
                if (through > 0) outbox.ack(rec.sid, through);
                if (rec.part >= MAX_PART) outbox.dropRecord(rec.sid);
                else outbox.nextPart(rec.sid);
            } else {
                outbox.ack(rec.sid, batch[batch.length - 1].ev.seq);
            }
            this.maxBatch.delete(rec.sid);
            return "continue";
        }
        if (status === 204 || (status === 403 && error === "read_only_impersonation")) {
            outbox.drop(rec.sid, seqsOf(batch), false);
            this.deps.onServerSuspend(rec.owner || "");
            return "stop";
        }
        if (status === 401) {
            const now = this.now();
            if (mayReissue && now - this.lastReissueAt >= REISSUE_MIN_MS) {
                this.lastReissueAt = now;
                if (await this.deps.reissue().catch(() => false)) return "continue";
            }
            this.backoff();
            return "stop";
        }
        if (status === 400 && (error === "bad_session" || error === "stale_session")) {
            outbox.dropRecord(rec.sid);
            this.deps.onStaleSession(rec.sid);
            return "continue";
        }
        if ((status === 413 || (status === 400 && error === "too_many_events")) && batch.length > 1) {
            this.maxBatch.set(rec.sid, Math.max(1, Math.floor(batch.length / 2)));
            return "continue";
        }
        if (status === 400 || status === 413) {
            outbox.drop(rec.sid, seqsOf(batch), true);
            this.deps.warn?.(`activity log: batch rejected (${status} ${error})`);
            return "continue";
        }
        if (status === 404 || status === 405) {
            // Маршрут ещё не выкачен — держим очередь и ждём по максимуму.
            this.notDeployed = true;
            this.backoffIdx = BACKOFF_MS.length - 2;
            this.backoff();
            return "stop";
        }
        if (status === 429 && error === "session_cap") {
            // За этот день новых документов сервер больше не заведёт.
            outbox.dropRecord(rec.sid);
            return "continue";
        }
        this.backoff();
        return "stop";
    }

    /**
     * Последний шанс при уходе со страницы: один пакет до 60 КБ маяком, а если
     * браузер его не принял — fetch с keepalive. Из очереди ничего не удаляется.
     */
    beacon(preferSid?: string): boolean {
        if (this.notDeployed && this.backingOff()) return false;
        const id = this.deps.identity(true);
        if (id.suspended || !id.email || !id.token) return false;
        const recs = this.deps.outbox.sendable(id.email);
        if (recs.length === 0) return false;
        const rec = recs.find((r) => r.sid === preferSid) || recs[recs.length - 1];
        let batch = this.deps.outbox.takeBatch(rec, this.maxBatch.get(rec.sid) ?? BATCH_EVENTS);
        let body = "";
        try {
            const header = this.deps.headerSync(rec);
            const build = () =>
                JSON.stringify({ token: id.token, session: header, events: batch.map((e) => e.ev) });
            body = build();
            while (utf8Bytes(body) > BODY_MAX_BYTES && batch.length > 1) {
                batch = batch.slice(0, Math.ceil(batch.length / 2));
                body = build();
            }
        } catch {
            return false;
        }
        let queued = false;
        try {
            const blob = new Blob([body], { type: CONTENT_TYPE });
            if (this.deps.beaconImpl) queued = this.deps.beaconImpl(this.deps.url, blob);
            else if (typeof navigator.sendBeacon === "function") {
                queued = navigator.sendBeacon(this.deps.url, blob);
            }
        } catch {
            queued = false;
        }
        if (!queued) {
            try {
                void this.fetchImpl(this.deps.url, {
                    method: "POST",
                    body,
                    keepalive: true,
                    credentials: "omit",
                    headers: { "Content-Type": CONTENT_TYPE },
                }).catch(() => undefined);
            } catch {
                return false;
            }
        }
        return true;
    }

    private backoff(): void {
        this.backoffIdx = Math.min(this.backoffIdx + 1, BACKOFF_MS.length - 1);
        this.nextAt = this.now() + BACKOFF_MS[this.backoffIdx];
    }

    private resetBackoff(): void {
        this.backoffIdx = -1;
        this.nextAt = 0;
        this.notDeployed = false;
    }
}
